import { memo, useEffect } from 'react';
import { Outlet, useMatches, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuthStore } from '@/store/useAuthStore';
import PageLoadingSkeleton from '@/shared/components/layout/PageLoadingSkeleton';
import type { RouteHandle } from '@/routes/types';
import type { AdminPermissionResponseDto } from '@api/sweetnya/model';

// 라우트 핸들의 권한 키 → /me 권한 객체(boolean 플래그) 매핑.
// SETTINGS 등 매핑이 없는 권한은 마스터 전용 영역으로 간주해 일반 관리자는 차단한다.
const PERMISSION_KEY_MAP: Record<string, keyof AdminPermissionResponseDto> = {
	DASHBOARD: 'dashboard',
	CONTENTS: 'contents',
	PARTNER: 'partner',
	EVENT: 'event',
};

// 권한 확인 Guard
const PermissionGuard = () => {

	const { isLoading, admin } = useAuthStore();
	const matches = useMatches();
	const navigate = useNavigate();

	useEffect(() => {
		if (isLoading || !admin) return;

		// 마스터 관리자는 모든 페이지 프리패스
		if (admin.master) return;

		const matchWithPermission = matches.find(m => (m?.handle as RouteHandle)?.permission);
		const permission = (matchWithPermission?.handle as RouteHandle)?.permission;

		if (!permission) return;

		const permissionKey = PERMISSION_KEY_MAP[permission];
		// 매핑된 키가 없으면 마스터 전용 → 차단. 있으면 해당 메뉴 권한 플래그 확인.
		const allowed = permissionKey ? admin.permissions?.[permissionKey] === true : false;

		if (!allowed) {
			toast.error('해당 페이지에 접근 권한이 없습니다.');
			navigate(-1);
		}
	}, [isLoading, admin, matches, navigate]);

	if (isLoading) {
		return <PageLoadingSkeleton />;
	}

	return <Outlet />;
};

export default memo(PermissionGuard);
